import React, { useEffect, useMemo, useReducer } from 'react';

import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  OutlinedInput,
  TextField,
  type TextFieldProps,
  Typography,
  debounce,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  type AutocompletePrediction,
  type AutocompletionRequest,
  type GeocoderResult,
  geocodePickedPlace,
} from '@realadvisor/hooks';
import { MapPin, Search } from 'lucide-react';
import type { Map } from 'mapbox-gl';
import type { UseFormSetValue } from 'react-hook-form';

import {
  onboardingOutlinedInputFocusedSx,
  onboardingOutlinedInputRootSx,
} from '@modules/_shared/components/create-transaction/onboardingFormStyles';
import {
  LocationMarker as LocationMarkerTS,
  Mapbox as MapboxTS,
} from '@modules/_shared/components/Mapbox';
import { useGoogleMapsApi } from '@modules/_shared/hooks/useGoogleMapsApi';
import { useLocale } from '@shared/hooks/locale';

const onboardingAddressOutlinedInputSx: SxProps<Theme> = {
  ...onboardingOutlinedInputRootSx,
  borderRadius: '10px',
  fontSize: 14,
  fontWeight: 500,
  '&.Mui-focused': onboardingOutlinedInputFocusedSx,
  '& .MuiOutlinedInput-input': {
    py: 1.125,
    px: 1.25,
  },
};

const onboardingTransactionSearchInputSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    ...onboardingOutlinedInputRootSx,
    '&.Mui-focused': onboardingOutlinedInputFocusedSx,
    fontSize: { xs: 16, md: 18 },
    '& .MuiOutlinedInput-input': {
      py: { xs: 1.25, md: 1.5 },
      pl: 1,
      fontWeight: 500,
    },
  },
};

export interface RaAddressType {
  street_number?: string | null;
  route?: string | null;
  locality?: string | null;
  postcode?: string | null;
  state?: string | null;
  country?: string | null;
  country_code?: string | null;
  lat?: number | null;
  lng?: number | null;
  google_place_id?: string;
}

type AddressKey = keyof RaAddressType;

type State = {
  inputValue?: string;
  address: RaAddressType;
  place_prediction: google.maps.places.AutocompletePrediction | null;
  place: google.maps.GeocoderResult | null;
  inputFocused?: boolean;
  previousGeocodedAddress: RaAddressType;
};

type Action =
  | { type: 'setInputValue'; inputValue: string }
  | { type: 'setAddress'; address: RaAddressType }
  | { type: 'setAddressKey'; key: AddressKey; value: string | number }
  | {
      type: 'setPlacePrediction';
      placePrediction: google.maps.places.AutocompletePrediction | null;
    }
  | { type: 'setPlace'; place: google.maps.GeocoderResult | null }
  | { type: 'setInputFocused'; inputFocused: boolean }
  | { type: 'setPreviousGeocodedAddress'; address: RaAddressType };

export type RaAddressInputProps = Omit<TextFieldProps, 'required'> & {
  region?: string;
  countryRestriction?: string;
  country?: string;
  setValue: UseFormSetValue<any>;
  path?: string;
  id: string;
  formValues?: any;
  includeGoogleFields?: boolean;
  requiredFields?: {
    street_number?: boolean;
    route?: boolean;
    country_code?: boolean;
    country?: boolean;
    locality?: boolean;
    postcode?: boolean;
    state?: boolean;
  };
  /** Debounce geocoding while typing (in ms). If set, geocode is triggered on change instead of only on blur. */
  debounceGeocodeMs?: number;
  uiVariant?: 'onboarding';
};

export const RaAddressInput = ({
  region,
  countryRestriction,
  setValue,
  size = 'small',
  disabled,
  path,
  formValues,
  includeGoogleFields = false,
  requiredFields,
  debounceGeocodeMs,
  uiVariant,
}: RaAddressInputProps) => {
  const { t } = useLocale();
  const isOnboarding = uiVariant === 'onboarding';
  const [options, setOptions] = React.useState<
    readonly google.maps.places.AutocompletePrediction[]
  >([]);
  const mapsApi = useGoogleMapsApi();
  const [servicesReady, setServicesReady] = React.useState(false);
  const autocompleteService = React.useRef<
    google.maps.places.AutocompleteService | undefined
  >(undefined);
  // A session groups all prediction requests of one search into a single
  // billable unit; discarded when the search ends (selection/clear/blur) so
  // the next search starts a fresh session.
  const autocompleteSessionToken = React.useRef<
    google.maps.places.AutocompleteSessionToken | undefined
  >(undefined);
  const geocoder = React.useRef<google.maps.Geocoder | undefined>(undefined);
  const mapbox = React.useRef<Map | null>(null);
  const mapboxTS = React.useRef<Map | null>(null);
  const formRef = React.useRef<HTMLFormElement | null>(null);
  const [shouldResubmit, setShouldResubmit] = React.useState(false);
  const [geocodeFailed, setGeocodeFailed] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const committedInputRef = React.useRef(
    formValues.google_place_description ?? '',
  );
  const inputValueRef = React.useRef(formValues.google_place_description ?? '');

  const [state, dispatch] = useReducer(
    (state: State, action: Action) => {
      switch (action.type) {
        case 'setInputValue':
          return { ...state, inputValue: action.inputValue };
        case 'setAddressKey':
          return {
            ...state,
            address: {
              ...state.address,
              [action.key]: action.value,
            },
          };
        case 'setAddress':
          return {
            ...state,
            address: { ...state.address, ...action.address },
          };
        case 'setPlacePrediction':
          return { ...state, place_prediction: action.placePrediction };
        case 'setPlace':
          return { ...state, place: action.place };
        case 'setInputFocused':
          return { ...state, inputFocused: action.inputFocused };
        case 'setPreviousGeocodedAddress':
          return {
            ...state,
            previousGeocodedAddress: {
              ...state.address,
              ...action.address,
            },
          };
        default:
          return state;
      }
    },
    {
      inputValue: formValues.google_place_description,
      address: {
        country: formValues.country,
        country_code: formValues.country_code,
        locality: formValues.locality,
        postcode: formValues.postcode,
        route: formValues.route,
        state: formValues.state,
        street_number: formValues.street_number,
        lat: formValues.lat,
        lng: formValues.lng,
        google_place_id: formValues.google_place_id,
      },
      place_prediction: null,
      place: null,
      inputFocused: false,
      previousGeocodedAddress: {
        country: formValues.country,
        country_code: formValues.country_code,
        locality: formValues.locality,
        postcode: formValues.postcode,
        route: formValues.route,
        state: formValues.state,
        street_number: formValues.street_number,
        lat: formValues.lat,
        lng: formValues.lng,
        google_place_id: formValues.google_place_id,
      },
    },
  );

  const geocode = React.useCallback(
    (
      {
        placeId,
        addressInput,
        sessionToken,
      }: {
        placeId?: string;
        addressInput?: string;
        sessionToken?: google.maps.places.AutocompleteSessionToken;
      },
      overrideAddress = false,
    ) => {
      if (!geocoder.current) {
        return;
      }
      const handlePlaces = (places: GeocoderResult[]) => {
        if (!places || places.length === 0) {
          setGeocodeFailed(true);
          return;
        }
        setGeocodeFailed(false);
        dispatch({ type: 'setPlace', place: places[0] });
        const place = places[0];
        const address: RaAddressType = {};

        // Parse address components more cleanly
        place.address_components.forEach(component => {
          const { types, long_name, short_name } = component;

          if (types.includes('route')) {
            address.route = long_name;
          }
          if (types.includes('street_number')) {
            address.street_number = long_name;
          }
          if (
            address.locality == null &&
            (types.includes('sublocality') ||
              types.includes('locality') ||
              types.includes('postal_town') ||
              types.includes('administrative_area_level_3'))
          ) {
            address.locality = long_name;
          }
          if (types.includes('postal_code')) {
            address.postcode = long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            address.state = long_name;
          }
          if (types.includes('country')) {
            address.country_code = short_name;
            address.country = long_name;
          }
        });

        address.lat = place.geometry.location.lat();
        address.lng = place.geometry.location.lng();
        address.google_place_id = place.place_id;

        if (overrideAddress) {
          // Update the previous geocoded address
          dispatch({
            type: 'setPreviousGeocodedAddress',
            address,
          });
          dispatch({ type: 'setAddress', address });
        } else {
          const previousAddress = { ...state.address };
          // Update the previous geocoded address since we don't want to override the address only the lat/lng
          dispatch({
            type: 'setPreviousGeocodedAddress',
            address: {
              ...previousAddress,
              lat: address.lat,
              lng: address.lng,
            },
          });
          dispatch({
            type: 'setAddress',
            address: {
              ...previousAddress,
              lat: address.lat,
              lng: address.lng,
            },
          });
        }
      };
      if (placeId != null) {
        // A picked prediction: Place Details with the session token closes
        // the autocomplete session in place of a geocode.
        geocodePickedPlace(
          google.maps,
          { placeId, sessionToken },
          handlePlaces,
        );
      } else {
        geocoder.current.geocode({ address: addressInput }, handlePlaces);
      }
    },
    [state.address],
  );

  // Helper functions for better readability
  const buildAddressString = React.useCallback((address: RaAddressType) => {
    return [
      address.street_number,
      address.route,
      address.locality,
      address.postcode,
      address.state,
      address.country,
    ]
      .filter(Boolean)
      .join(', ');
  }, []);

  const areRequiredFieldsFilled = React.useCallback(
    (address: RaAddressType) => {
      return (
        !!address.route?.trim() &&
        !!address.postcode?.trim() &&
        !!address.locality?.trim()
      );
    },
    [],
  );

  const hasAddressChanged = React.useCallback(
    (current: RaAddressType, previous?: RaAddressType) => {
      if (!previous) {
        return true;
      }

      const fieldsToCompare: (keyof RaAddressType)[] = [
        'street_number',
        'route',
        'locality',
        'postcode',
        'country',
      ];

      return fieldsToCompare.some(field => current[field] !== previous[field]);
    },
    [],
  );

  const validateAndGeocode = React.useCallback(() => {
    if (!areRequiredFieldsFilled(state.address)) {
      return;
    }

    if (hasAddressChanged(state.address, state.previousGeocodedAddress)) {
      const addressString = buildAddressString(state.address);
      // We override the complete address only if we never geocoded before or the initial state is empty
      geocode(
        { addressInput: addressString },
        state.previousGeocodedAddress.lat == null,
      );
    }
  }, [
    state.address,
    state.previousGeocodedAddress,
    geocode,
    buildAddressString,
    areRequiredFieldsFilled,
    hasAddressChanged,
  ]);

  // Store parent form reference on mount
  useEffect(() => {
    const formElement = rootRef.current?.closest('form');
    if (formElement) {
      formRef.current = formElement as HTMLFormElement;
    }
  }, []);

  // Keep a ref to the latest validateAndGeocode so debounce stays stable
  const validateAndGeocodeRef = React.useRef(validateAndGeocode);
  React.useEffect(() => {
    validateAndGeocodeRef.current = validateAndGeocode;
  });

  // Debounced geocode for onChange (when debounceGeocodeMs is set)
  // The debounce function itself is stable, but calls the latest validateAndGeocode via ref
  const debouncedValidateAndGeocode = useMemo(
    () =>
      debounceGeocodeMs
        ? debounce(() => validateAndGeocodeRef.current(), debounceGeocodeMs)
        : undefined,
    [debounceGeocodeMs],
  );

  // Helper to render address input fields consistently
  const renderAddressField = React.useCallback(
    (
      field: keyof NonNullable<RaAddressInputProps['requiredFields']>,
      placeholder: string,
      props: Partial<React.ComponentProps<typeof OutlinedInput>> = {},
    ) => (
      <OutlinedInput
        fullWidth
        sx={
          isOnboarding
            ? onboardingAddressOutlinedInputSx
            : { background: 'var(--mui-palette-background-paper)' }
        }
        size="small"
        placeholder={placeholder}
        value={state.address?.[field] || ''}
        inputProps={{
          required:
            requiredFields?.[field] ??
            ['route', 'postcode', 'locality'].includes(field),
        }}
        disabled={disabled}
        onChange={e => {
          dispatch({
            type: 'setAddressKey',
            key: field,
            value: e.target.value,
          });
          // Trigger debounced geocode on change if enabled
          debouncedValidateAndGeocode?.();
        }}
        onBlur={validateAndGeocode}
        {...props}
      />
    ),
    [
      state.address,
      requiredFields,
      disabled,
      validateAndGeocode,
      debouncedValidateAndGeocode,
      isOnboarding,
    ],
  );

  useEffect(() => {
    // useGoogleMapsApi only resolves once importLibrary('places') has completed,
    // so the places / geocoding namespaces are guaranteed attached here.
    if (!mapsApi || autocompleteService.current) {
      return;
    }
    autocompleteService.current = new mapsApi.places.AutocompleteService();
    geocoder.current = new mapsApi.Geocoder();
    // Flip state so the prediction effect re-runs deterministically once the
    // services exist (the refs alone don't trigger a re-render).
    setServicesReady(true);
  }, [mapsApi]);

  // Fill form values on address change
  useEffect(() => {
    for (const key in state.address) {
      const addressKey = key as AddressKey;
      if (!includeGoogleFields && addressKey === 'google_place_id') {
        continue;
      }
      setValue([path, addressKey].join('.'), state.address[addressKey], {
        shouldDirty: true,
      });
    }
    if (includeGoogleFields) {
      setValue([path, 'google_place_description'].join('.'), state.inputValue, {
        shouldDirty: true,
      });
    }
  }, [path, state.address, state.inputValue, setValue, includeGoogleFields]);

  // Declare debounced function to fetch predictions
  const fetchPredictions = useMemo(
    () =>
      debounce(
        (
          request: AutocompletionRequest,
          callback: (results?: readonly AutocompletePrediction[]) => void,
        ) => {
          if (!autocompleteService.current) {
            return;
          }
          // the service only exists once the places library is attached,
          // so the constructor is safe to reach through the global namespace
          autocompleteSessionToken.current ??=
            new google.maps.places.AutocompleteSessionToken();
          autocompleteService.current.getPlacePredictions(
            { ...request, sessionToken: autocompleteSessionToken.current },
            callback,
          );
        },
        275,
      ),
    [],
  );

  // Fetch Predictions on input change or focus
  useEffect(() => {
    let active = true;

    // If input is not focused, then we don't fetch predictions
    if (!state.inputFocused) {
      return;
    }

    // If no autocompleteService, then we can't do anything
    if (!autocompleteService.current) {
      return;
    }
    // If no input value, then we empty the options and return
    if (!state.inputValue || state.inputValue === '') {
      // input cleared — the search is over, next one starts a new session
      autocompleteSessionToken.current = undefined;
      return setOptions([]);
    }
    // Focus of an already-committed address is not a new search. Agents open
    // lead/property forms far more often than they retype the address.
    if (state.inputValue === committedInputRef.current) {
      return;
    }

    const request = {
      input: state.inputValue,
      region,
      componentRestrictions: countryRestriction
        ? { country: countryRestriction }
        : undefined,
    } as google.maps.places.AutocompletionRequest;

    fetchPredictions(request, (results?: readonly AutocompletePrediction[]) => {
      if (!active) {
        return;
      }
      let newOptions: readonly AutocompletePrediction[] = [];
      if (results) {
        newOptions = [...newOptions, ...results];
      }
      setOptions(newOptions);
    });

    return () => {
      active = false;
      // select/blur unmounts this effect; cancel the trailing debounce so it
      // cannot mint a fresh 1-request session after the token was discarded.
      fetchPredictions.clear();
    };
  }, [
    state.inputValue,
    state.inputFocused,
    fetchPredictions,
    countryRestriction,
    region,
    servicesReady,
  ]);

  // Calculate validation state for hidden field
  const addressHasChanged = hasAddressChanged(
    state.address,
    state.previousGeocodedAddress,
  );
  const hasCoordinates = !!(state.address.lat && state.address.lng);
  const requiresGeocoding =
    addressHasChanged && areRequiredFieldsFilled(state.address);
  const isGeocodedValid =
    !requiresGeocoding ||
    (hasCoordinates && !addressHasChanged) ||
    geocodeFailed;

  // Auto-resubmit when geocoding completes and validation becomes valid
  useEffect(() => {
    if (shouldResubmit && isGeocodedValid) {
      setShouldResubmit(false);
      setTimeout(() => {
        formRef.current?.requestSubmit();
      }, 50);
    }
  }, [shouldResubmit, isGeocodedValid]);

  const handleInvalid = React.useCallback(
    (e: React.InvalidEvent<HTMLInputElement>) => {
      e.preventDefault();
      // Auto-trigger geocoding when validation fails
      if (requiresGeocoding) {
        validateAndGeocode();
        setShouldResubmit(true);
      }
    },
    [requiresGeocoding, validateAndGeocode],
  );

  return (
    <Grid
      container
      spacing={2}
      ref={rootRef}
      sx={{ alignItems: isOnboarding ? 'stretch' : undefined }}
    >
      <Grid size={{ xs: 12, md: 6 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete<
              google.maps.places.AutocompletePrediction,
              false,
              false,
              false
            >
              isOptionEqualToValue={(option, value) =>
                option.place_id === value.place_id
              }
              getOptionLabel={option => option.description}
              filterOptions={x => x}
              options={options}
              autoComplete={false}
              disabled={disabled}
              includeInputInList
              filterSelectedOptions
              autoHighlight
              popupIcon={null}
              clearIcon={null}
              blurOnSelect
              onFocus={() => {
                dispatch({ type: 'setInputFocused', inputFocused: true });
              }}
              clearOnBlur={false}
              onBlur={() => {
                // Do not commit free-typed text into the form. Revert to the
                // last selected/initial description so google_place_description
                // stays a resolved place (or the value we loaded), not an
                // unvalidated query. An emptied input is an explicit clear
                // (there is no clear icon) — keep '' rather than restoring.
                const nextValue =
                  inputValueRef.current === '' ? '' : committedInputRef.current;
                committedInputRef.current = nextValue;
                inputValueRef.current = nextValue;
                dispatch({
                  type: 'setInputValue',
                  inputValue: nextValue,
                });
                autocompleteSessionToken.current = undefined;
                dispatch({ type: 'setInputFocused', inputFocused: false });
              }}
              value={null}
              inputValue={state.inputValue ?? ''}
              onChange={(
                _event,
                newValue: google.maps.places.AutocompletePrediction | null,
              ) => {
                const sessionToken = autocompleteSessionToken.current;
                autocompleteSessionToken.current = undefined;
                const description = newValue?.description ?? '';
                committedInputRef.current = description;
                inputValueRef.current = description;
                // Set the controlled value here so selection still populates
                // the input if MUI reports this change as reason 'reset'
                // (which onInputChange ignores) instead of 'selectOption'.
                dispatch({ type: 'setInputValue', inputValue: description });
                setOptions(newValue ? [newValue] : []);
                dispatch({
                  type: 'setPlacePrediction',
                  placePrediction: newValue,
                });
                if (newValue) {
                  geocode(
                    {
                      placeId: newValue.place_id,
                      sessionToken,
                    },
                    true,
                  );
                }
              }}
              onInputChange={(_event, newInputValue, reason) => {
                // MUI's input handleBlur runs before Autocomplete onBlur and,
                // with value={null}, pushes '' with reason 'blur'/'reset'.
                // Ignore those so we neither persist '' nor clobber the live
                // ref. Selection is applied in onChange (not via this reason).
                if (reason === 'blur' || reason === 'reset') {
                  return;
                }
                inputValueRef.current = newInputValue;
                dispatch({
                  type: 'setInputValue',
                  inputValue: newInputValue,
                });
                if (newInputValue === '' || reason === 'clear') {
                  committedInputRef.current = '';
                  autocompleteSessionToken.current = undefined;
                }
              }}
              size={size}
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  size={size}
                  sx={
                    isOnboarding
                      ? { width: '100%', ...onboardingTransactionSearchInputSx }
                      : undefined
                  }
                  style={{
                    paddingRight: 0,
                    background: isOnboarding
                      ? undefined
                      : 'var(--mui-palette-background-paper)',
                  }}
                  placeholder={t('Search address...')}
                  slotProps={{
                    input: {
                      ...params.slotProps.input,
                      sx: { background: 'var(--mui-palette-background-paper)' },
                      startAdornment: (
                        <Search size={18} style={{ marginLeft: 4 }} />
                      ),
                    },

                    htmlInput: {
                      ...params.slotProps.htmlInput,
                      value: state.inputValue || '',
                      spellCheck: false,
                      autoCorrect: 'off',
                      autoComplete: 'off',
                      autoCapitalize: 'off',
                      role: 'presentation',
                    },
                  }}
                />
              )}
              renderOption={(props, option) =>
                renderOption(props, option, isOnboarding)
              }
            />
          </Grid>

          <Grid size={9}>
            <FormControl fullWidth>
              {renderAddressField('route', t('Street name'), {
                name: 'route',
                autoComplete: 'street-address',
              })}
            </FormControl>
          </Grid>
          <Grid size={3}>
            <FormControl fullWidth>
              {renderAddressField('street_number', t('House #'), {
                name: 'street_number',
                autoComplete: 'street-number',
              })}
            </FormControl>
          </Grid>
          <Grid size={3}>
            <FormControl fullWidth>
              {renderAddressField('postcode', t('Postal code'), {
                name: 'postcode',
                autoComplete: 'postal-code',
              })}
            </FormControl>
          </Grid>
          <Grid size={9}>
            <FormControl fullWidth>
              {renderAddressField('locality', t('Locality'), {
                name: 'locality',
                autoComplete: 'address-level2',
              })}
            </FormControl>
          </Grid>
          <Grid size={isOnboarding ? 6 : 5}>
            <FormControl fullWidth>
              {renderAddressField('state', t('State'), {
                name: 'state',
                autoComplete: 'address-level1',
              })}
            </FormControl>
          </Grid>
          {!isOnboarding && (
            <Grid size={2}>
              <FormControl fullWidth>
                {renderAddressField('country_code', t('Country code'), {
                  name: 'country_code',
                  autoComplete: 'country_code',
                })}
              </FormControl>
            </Grid>
          )}
          <Grid size={isOnboarding ? 6 : 5}>
            <FormControl fullWidth>
              {renderAddressField('country', t('Country'), {
                name: 'country',
                autoComplete: 'country',
              })}
            </FormControl>
          </Grid>
          {includeGoogleFields && (
            <Grid size={6}>
              <FormControl fullWidth>
                <OutlinedInput
                  sx={{ background: 'var(--mui-palette-background-paper)' }}
                  fullWidth
                  name="google_place_id"
                  size="small"
                  placeholder={t('Google place id')}
                  value={state.address.google_place_id || ''}
                  onChange={e =>
                    dispatch({
                      type: 'setAddressKey',
                      key: 'google_place_id',
                      value: e.target.value,
                    })
                  }
                />
              </FormControl>
            </Grid>
          )}
          {!isOnboarding && (
            <>
              <Grid size={3}>
                <FormControl fullWidth>
                  <OutlinedInput
                    sx={{ background: 'var(--mui-palette-background-paper)' }}
                    fullWidth
                    name="lat"
                    size="small"
                    placeholder={t('Latitude')}
                    value={formValues.lat || ''}
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid size={3}>
                <FormControl fullWidth>
                  <OutlinedInput
                    sx={{ background: 'var(--mui-palette-background-paper)' }}
                    fullWidth
                    name="lng"
                    size="small"
                    placeholder={t('Longitude')}
                    value={formValues.lng || ''}
                    disabled
                  />
                </FormControl>
              </Grid>
            </>
          )}
          {/* Hidden validation field - ensures address is geocoded before submit */}
          <input
            name={`${path}.__geocoded`}
            onChange={() => {}}
            value={isGeocodedValid ? 'true' : ''}
            required
            style={{ visibility: 'hidden' }}
            onInvalid={handleInvalid}
          />
        </Grid>
      </Grid>
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={
          isOnboarding
            ? {
                display: 'flex',
                alignSelf: 'stretch',
              }
            : undefined
        }
      >
        <Box
          sx={
            isOnboarding
              ? {
                  width: '100%',
                  position: 'relative',
                  flex: 1,
                  height: { xs: 210, md: '100%' },
                  minHeight: { md: 0 },
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1.5px solid',
                  borderColor: 'grey.300',
                  bgcolor: 'grey.200',
                  '& .map-container': {
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                  },
                  '& .mapboxgl-map, & .mapboxgl-canvas': {
                    width: '100% !important',
                    height: '100% !important',
                  },
                }
              : { height: 300 }
          }
        >
          <MapboxTS
            mapRef={mapboxTS}
            style={
              isOnboarding
                ? { width: '100%', height: '100%' }
                : { borderRadius: 8 }
            }
            lat={state.address?.lat ?? undefined}
            lng={state.address?.lng ?? undefined}
            zoom={state.address?.lat ? 16 : 10}
          >
            {state.address.lat && (
              <LocationMarkerTS
                lat={state.address?.lat || 0}
                lng={state.address?.lng || 0}
                onDragend={latLng => {
                  mapboxTS.current?.panTo(latLng);
                  mapbox.current?.panTo(latLng);
                  setValue([path, 'lat'].join('.'), latLng.lat, {
                    shouldDirty: true,
                  });
                  setValue([path, 'lng'].join('.'), latLng.lng, {
                    shouldDirty: true,
                  });
                }}
              />
            )}
          </MapboxTS>
        </Box>
      </Grid>
    </Grid>
  );
};

const renderOption = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: google.maps.places.AutocompletePrediction,
  isOnboarding: boolean,
) => {
  return (
    <li {...props} key={option.place_id}>
      <Grid
        container
        sx={{
          alignItems: 'center',
        }}
      >
        <Grid sx={{ display: 'flex', width: 44 }}>
          <MapPin size={20} style={{ color: 'inherit', opacity: 0.6 }} />
        </Grid>
        <Grid
          sx={{
            ...(isOnboarding
              ? { minWidth: 0, flex: 1 }
              : { width: 'calc(100% - 44px)' }),
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {option.structured_formatting.main_text}

          <Typography
            variant="body2"
            style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
            sx={{
              color: 'text.secondary',
            }}
          >
            {option.structured_formatting.secondary_text}
          </Typography>
        </Grid>
      </Grid>
    </li>
  );
};
